import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import axios from 'axios';
import { C } from '../theme';
import { countries, getCitiesByCountry, City } from '../data/locations';
import { useAuth } from '../contexts/AuthContext';
import LocationPicker from '../components/LocationPicker';

const businessTypes = [
  'Restoran i ugostiteljstvo',
  'Trgovina i usluge',
  'Proizvodnja',
  'Tehnologija i IT',
  'Zdravstvo',
  'Obrazovanje',
  'Transport i logistika',
  'Nekretnine',
  'Finansije i bankarstvo',
  'Ostalo',
];

const businessTypeMap: Record<string, string> = {
  'Restoran i ugostiteljstvo': 'restaurant_hospitality',
  'Trgovina i usluge': 'retail_services',
  'Proizvodnja': 'manufacturing',
  'Tehnologija i IT': 'technology_it',
  'Zdravstvo': 'healthcare',
  'Obrazovanje': 'education',
  'Transport i logistika': 'transport_logistics',
  'Nekretnine': 'real_estate',
  'Finansije i bankarstvo': 'finance_banking',
  'Ostalo': 'other',
};

const CreateBusinessAd: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [countryId, setCountryId] = useState('');
  const [cityId, setCityId] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [establishedYear, setEstablishedYear] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    // Redirect non-authenticated users to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user is a business owner
    if (user && user.userType !== 'business_owner') {
      setError('Samo vlasnici biznisa mogu kreirati oglase za biznis');
    }
  }, [isAuthenticated, navigate, user]);

  useEffect(() => {
    if (countryId) {
      setAvailableCities(getCitiesByCountry(Number(countryId)));
      setCityId('');
    } else {
      setAvailableCities([]);
    }
  }, [countryId]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      if (images.length + newFiles.length > 10) {
        setError('Možete dodati maksimalno 10 slika');
        return;
      }

      setImages(prev => [...prev, ...newFiles]);
      
      // Create preview URLs
      const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      const newUrls = [...prev];
      URL.revokeObjectURL(newUrls[index]);
      return newUrls.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate required fields
    if (!title || !description || !price || !businessType || !countryId || !cityId) {
      setError('Molimo popunite sva obavezna polja');
      setLoading(false);
      return;
    }

    // Validate numeric IDs for country and city
    if (isNaN(Number(countryId)) || isNaN(Number(cityId))) {
      setError('Molimo izaberite državu i grad iz liste. (ID mora biti broj)');
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('price', String(Number(price)));
      formData.append('business_type', businessTypeMap[businessType] || businessType);
      formData.append('country_id', String(Number(countryId)));
      formData.append('city_id', String(Number(cityId)));
      
      if (annualRevenue) {
        formData.append('annual_revenue', String(Number(annualRevenue)));
      }
      if (employeeCount) {
        formData.append('employee_count', String(Number(employeeCount)));
      }
      if (establishedYear) {
        formData.append('established_year', String(Number(establishedYear)));
      }
      if (location) {
        formData.append('latitude', String(location.lat));
        formData.append('longitude', String(location.lng));
      }

      images.forEach(image => {
        formData.append('images', image);
      });

      const token = localStorage.getItem('token');
      
      // Debug logging
      console.log('Submitting form data:');
      console.log('FormData entries:');
      // Use Array.from to avoid TypeScript iteration issues
      Array.from(formData.entries()).forEach(([key, value]) => {
        console.log(`${key}:`, value);
      });
      
      console.log('Images array:', images);
      console.log('FormData object:', formData);
      
      const response = await axios.post(
        '/api/business-ads',
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      
             console.log('Response received:', response);
       
       setSuccess('Oglas za biznis je uspešno kreiran!');
       setTimeout(() => {
         navigate('/business');
       }, 1500);
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Greška pri kreiranju oglasa za biznis');
      }
    } finally {
      setLoading(false);
    }
  };

  if (user && user.userType !== 'business_owner') {
    return (
      <Box sx={{ pt: '110px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Alert severity="error" sx={{ mt: 4 }}>
            Samo vlasnici biznisa mogu kreirati oglase za biznis. Molimo se prijavite kao vlasnik biznisa.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: '110px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 600,
              mb: 2,
              color: C.white,
            }}
          >
            Postavi oglas za biznis
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: C.muted,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Kreirajte privlačan oglas za vaš biznis i povežite se sa pravim investitorima
          </Typography>
        </Box>

        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 5 },
            backgroundColor: C.surface,
            borderRadius: 4,
            border: `1px solid ${C.line}`,
          }}
        >
                     {error && (
             <Alert severity="error" sx={{ mb: 4 }}>
               {error}
             </Alert>
           )}
           
           {success && (
             <Alert severity="success" sx={{ mb: 4 }}>
               {success}
             </Alert>
           )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Osnovne informacije
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Naslov oglasa"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opis biznisa"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  multiline
                  rows={4}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cena"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  required
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EuroIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tip biznisa</InputLabel>
                  <Select
                    value={businessType}
                    label="Tip biznisa"
                    onChange={(e) => setBusinessType(e.target.value)}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {businessTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Država</InputLabel>
                  <Select
                    value={countryId}
                    label="Država"
                    onChange={(e) => setCountryId(e.target.value)}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Grad</InputLabel>
                  <Select
                    value={cityId}
                    label="Grad"
                    onChange={(e) => setCityId(e.target.value)}
                    disabled={!countryId}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {availableCities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Dodatne informacije
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Godišnji prihod"
                  value={annualRevenue}
                  onChange={(e) => setAnnualRevenue(e.target.value)}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EuroIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Broj zaposlenih"
                  value={employeeCount}
                  onChange={(e) => setEmployeeCount(e.target.value)}
                  type="number"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Godina osnivanja"
                  value={establishedYear}
                  onChange={(e) => setEstablishedYear(e.target.value)}
                  type="number"
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: C.white }}>
                  Lokacija na mapi
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: C.muted }}>
                  Opciono. Lokacija je vidljiva samo vama i investitorima.
                </Typography>
                <LocationPicker value={location} onChange={setLocation} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: C.white }}>
                  Slike
                </Typography>
                <Typography variant="body2" sx={{ color: C.muted, mb: 2 }}>
                  Dodajte slike vašeg biznisa (maksimalno 10)
                </Typography>
                
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  multiple
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AddPhotoAlternateIcon />}
                    sx={{ mb: 2 }}
                  >
                    Dodaj slike
                  </Button>
                </label>

                {previewUrls.length > 0 && (
                  <ImageList sx={{ width: '100%', height: 450 }} cols={3} rowHeight={164}>
                    {previewUrls.map((url, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          loading="lazy"
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          actionIcon={
                            <IconButton
                              sx={{ color: 'white' }}
                              onClick={() => removeImage(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      background: C.goldGrad,
                      px: 6,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#333333',
                      },
                      '&:disabled': {
                        backgroundColor: '#cccccc',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Postavi oglas'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CreateBusinessAd;
